import { useState } from 'react';
import { MonitoringGoodsDetailDaily } from '@/types/api';
import TrashIcon from '@/assets/icons/Monitoring/Trash_1.svg?react';
import { usePost, CPLAT_API_ROUTES } from '@/api';
import { useAppSelector } from '@/redux/store';
import { useToast } from '@/hooks/V3/useToast';
import { useMonitoringGoods } from './MonitoringContext';

// 예시 props: selectedGoods.MonitoringGoodsDetailDailyList
const GoodsDetailKeywordTable = ({
  dailyList,
  selectedGoods,
  isMockGoods,
}: {
  dailyList: MonitoringGoodsDetailDaily[];
}) => {
  if (!dailyList || dailyList.length === 0) return <div>데이터가 없습니다.</div>;
  const user = useAppSelector((state) => state.user);
  const { refetch } = useMonitoringGoods();
  const { showToast } = useToast();
  const [hoveredCol, setHoveredCol] = useState<number | null>(null);

  // 날짜(컬럼) 추출 (최신순 정렬)
  const dateList = dailyList.map((d) => d.MonitoringDate?.slice(0, 10)).filter(Boolean);

  // 키워드(행) 추출 — 현재 구독 중인 키워드 기준 (daily 스냅샷은 수집 시점 히스토리라 삭제 즉시 반영 안 됨)
  const keywordList: string[] = selectedGoods?.RelatedKeyword ?? [];

  // 키워드별 날짜별 데이터 매핑
  const getKeywordRank = (day: any, keyword: string) => {
    const kw = day.RelatedKeywordsRanking.find((k: any) => k.Keyword === keyword);
    if (!kw) return { rank: '-', status: '', range: 0 };
    return {
      rank: kw.Rank > 0 ? `${kw.Rank}위` : '1000위 밖',
      status: kw.RankingChangedStatus,
      range: kw.RankingChangedRange,
    };
  };

  // 키워드 변경 mutation (API 엔드포인트/파라미터는 실제 명세에 맞게)
  const { mutate: updateKeywords, isPending: isUpdating } = usePost(
    CPLAT_API_ROUTES.setUserMonitoringGoods,
  );
  const handleRemoveKeywords = (keyword: string) => {
    if (isMockGoods) {
      showToast('warning', '예시 상품의 키워드는 삭제할 수 없어요');
      return;
    }
    // 키워드 삭제 로직 구현
    updateKeywords(
      {
        cplatToken: user.CplatToken,
        StoreName: selectedGoods.StoreName,
        GoodsNo: selectedGoods.GoodsNo,
        GoodsImageUrl: selectedGoods.GoodsImageUrl,
        GoodsName: selectedGoods.GoodsName,
        CategoryFullName: selectedGoods.CategoryFullName,
        UseYn: 'Y',
        RelatedKeywordList: selectedGoods.RelatedKeyword.filter((k: string) => k !== keyword),
        GoodsUrl: selectedGoods.GoodsUrl,
        GoodsPrice: selectedGoods.GoodsPrice,
        relatedKeywordRankInfo: {}, // 셀러박스에서는 변화량 데이터 빈 객체로 전달, 1일 이후 자동 수집
        ShippingFee: selectedGoods.ShippingFee,
        GoodsRegisterDate: selectedGoods.GoodsRegisterDate,
        NoticeYn: selectedGoods.NoticeYn,
        IsMyStoreGoods: selectedGoods.IsMyStoreGoods,
        MallSeq: selectedGoods.MallSeq,
        InsertYn: 'N', // 기존 등록된 상품이므로 N
      },
      {
        onSuccess: () => {
          showToast('success', '키워드가 삭제되었어요. 반영까지 최대 하루가 소요될 수 있어요.');
          refetch();
        },
        onError: () => {
          showToast('warning', '키워드 삭제에 실패했습니다. 다시 시도해주세요.');
        },
      },
    );
  };

  return (
    <table
      className="w-full table-fixed border-collapse text-gr-800"
      style={{ minWidth: 180 + dateList.length * 120 }}
    >
      <thead>
        <tr className="sticky top-0 z-[10] rounded-t-[8px] bg-gr-50 Caption1M12">
          <th className="sticky left-0 w-[180px] min-w-[180px] max-w-[180px] overflow-hidden text-ellipsis whitespace-nowrap border-b border-r bg-gr-50 px-3 py-2 text-left">
            키워드
          </th>
          {dateList.map((date, idx) => (
            <th
              key={`${date}-${idx}`}
              onMouseEnter={() => setHoveredCol(idx)}
              onMouseLeave={() => setHoveredCol(null)}
              className={`w-[120px] min-w-[120px] max-w-[120px] overflow-hidden text-ellipsis whitespace-nowrap border-b px-[10px] py-2 text-left Caption1M12 ${
                hoveredCol === idx ? 'bg-point-100' : 'bg-gr-50'
              }`}
            >
              {formatDateWithDay(date)}
            </th>
          ))}
          <th aria-hidden className="border-b bg-gr-50" />
        </tr>
      </thead>

      <tbody>
        {keywordList.map((keyword: string) => (
          <tr key={keyword}>
            <td
              title={keyword}
              className="sticky left-0 w-[180px] min-w-[180px] max-w-[180px] overflow-hidden text-ellipsis whitespace-nowrap border-b border-r bg-white px-3 py-2 text-gr-800 Body5M14"
            >
              <div className="flex w-full items-center justify-between">
                <span className="overflow-hidden text-ellipsis whitespace-nowrap">{keyword}</span>

                <button
                  type="button"
                  className="flex h-[34px] w-[34px] items-center justify-center rounded-[8px] border py-[8px] text-gr-400 hover:bg-white/90"
                  onClick={() => {
                    handleRemoveKeywords(keyword);
                  }}
                  aria-label="삭제"
                >
                  <TrashIcon />
                </button>
              </div>
            </td>
            {dailyList.map((day: MonitoringGoodsDetailDaily, idx: number) => {
              const { rank, status, range } = getKeywordRank(day, keyword);
              const suffix =
                status === 'up'
                  ? ` (▲${Math.abs(range)})`
                  : status === 'down'
                    ? ` (▼${Math.abs(range)})`
                    : status === 'stay'
                      ? ' (-)'
                      : '';
              return (
                <td
                  key={idx}
                  title={`${rank}${suffix}`}
                  onMouseEnter={() => setHoveredCol(idx)}
                  onMouseLeave={() => setHoveredCol(null)}
                  className={`w-[120px] min-w-[120px] max-w-[120px] overflow-hidden text-ellipsis whitespace-nowrap border-b px-[10px] py-2 text-left text-gr-800 Body5M14 ${
                    hoveredCol === idx ? 'bg-point-100' : ''
                  }`}
                >
                  {rank}
                  {suffix && <span className="ml-1">{suffix.trimStart()}</span>}
                </td>
              );
            })}
            <td aria-hidden className="border-b" />
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default GoodsDetailKeywordTable;

// 요일 한글 배열
const WEEKDAY_KO = ['일', '월', '화', '수', '목', '금', '토'];

function formatDateWithDay(dateStr: string) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const weekday = WEEKDAY_KO[date.getDay()];
  return `${month}.${day}(${weekday})`;
}
